/**
 * RoutineStepCard — modern, guided, single-step UX for the Routine Checker.
 *
 * Behavior contract:
 *   • Input is visible immediately; no "do you use X?" gating.
 *   • Helper text underneath: "If you don't use this, you can skip this step."
 *   • Typing surfaces 2–4 smart suggestions:
 *       - Empty / broad keyword ("cleanser", "moisturizer") → curated picks
 *         for the current slot.
 *       - Partial brand / product name → live search via `/api/products/search`.
 *   • Selecting a suggestion shows a compact selected-product card with
 *     image + name + brand.
 *   • Bottom action: one forward control — [Skip step] when empty, [Next]
 *     once a catalog product is selected or an ingredient list is pasted, or
 *     [Check routine] on the final step. (A bare typed name that was never
 *     picked/pasted can't be evaluated, so the step still reads [Skip step].)
 *   • Real product images only — when product.image_url is empty we render
 *     a clean brand-initial monogram. No synthetic / generated images.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronRight, Check, Search, Sparkles, X as XIcon, Image as ImageIcon } from "lucide-react";
import { Button } from "./ui/button";
import { resolveProductImage } from "../lib/api";
import { cn } from "../lib/utils";
import { rankProducts, sortByPrestige, suggestBrand } from "../lib/routineRanking";
import { splitBrandAndName } from "../lib/productDisplay";

// Module-level cache: product.id → resolved image URL (or null if not found).
// Survives across re-mounts within a session, so re-selecting the same
// product feels instant.
const _IMG_CACHE = new Map();

// Slot → keyword tokens that classify a product into this routine slot.
// Used both to surface curated picks AND to hard-filter the search subset
// so a brand query never leaks across categories (e.g. "CeraVe" in the SPF
// step never returns CeraVe Moisturizing Cream).
const SLOT_KEYWORDS = {
  cleanser:    ["cleanser", "wash", "foam", "cleansing", "soap", "gel cleanser"],
  toner:       ["toner", "essence", "exfoliant", "bha", "aha", "glycolic", "salicylic toner"],
  serum:       ["serum", "ampoule", "treatment", "concentrate", "vitamin c", "niacinamide",
                "retinol", "azelaic", "discoloration", "framboos"],
  moisturizer: ["moisturizer", "moisturiser", "cream", "lotion", "gel-cream",
                "hydrating cream", "water gel", "eczema relief", "facial moisturizer"],
  spf:         ["spf", "sunscreen", "sun cream", "uv", "sun lotion", "anthelios", "sunblock"],
};

// Negative tokens — products that match a slot's positive keyword but should
// NOT be classified into that slot when these terms appear (prevents
// "moisturizer" → matching "Moisturizing Wash" cleansers, etc).
const SLOT_NEGATIVES = {
  cleanser:    ["cream", "lotion", "moistur", "serum", "spf", "sunscreen"],
  toner:       ["cream", "lotion", "moistur", "spf", "sunscreen"],
  serum:       ["cleanser", "wash", "spf", "sunscreen"],
  moisturizer: ["cleanser", "wash", "serum", "spf", "sunscreen", "exfoliant"],
  spf:         ["cleanser", "wash", "serum", "moistur", "cream"],
};

function classifyProductSlot(product, slot) {
  const blob = `${product.full_name || ""} ${product.name || ""}`.toLowerCase();
  const positives = SLOT_KEYWORDS[slot] || [];
  if (!positives.some((k) => blob.includes(k))) return false;
  const negatives = SLOT_NEGATIVES[slot] || [];
  // Sunscreen is the canonical SPF marker — let it override the cream/lotion negatives.
  if (slot === "spf") return true;
  // For all other slots, reject if a negative token is present UNLESS the
  // positive token itself contains the negative (e.g. "water gel" positive
  // for moisturizer contains "gel" — already handled by exact-match check).
  if (negatives.some((n) => blob.includes(n) && !positives.some((p) => p.includes(n)))) {
    // exception: serum step should keep "treatment" products even if they
    // have "cream" in the long name? The catalog doesn't currently exhibit
    // this case; rejecting is safer until counter-examples appear.
    return false;
  }
  return true;
}

// Tiny Levenshtein distance — retained for any legacy callers inside this
// file; modern ranking lives in ../lib/routineRanking.js.

function brandInitial(brand) {
  return (brand || "?").trim().charAt(0).toUpperCase();
}

function ProductMonogram({ brand, image_url, size = "md" }) {
  const dim = size === "sm" ? "w-10 h-10" : "w-14 h-14";
  if (image_url) {
    return (
      <img
        src={image_url}
        alt={brand || "Product"}
        className={cn(dim, "rounded-xl object-cover bg-cream-100 border border-surface-line shrink-0")}
        loading="lazy"
        onError={(e) => { e.currentTarget.style.display = "none"; }}
      />
    );
  }
  return (
    <span
      aria-hidden
      className={cn(
        dim,
        "rounded-xl bg-gradient-to-br from-cream-100 to-peach-50",
        "border border-surface-line shrink-0",
        "inline-flex items-center justify-center font-bold text-ink-soft tracking-tight",
        size === "sm" ? "text-base" : "text-xl",
      )}
    >
      {brandInitial(brand)}
    </span>
  );
}

export default function RoutineStepCard({
  slot,
  label,
  hint,
  stepNumber,
  totalSteps,
  isLast,
  selected,           // { id, name, brand, full_name, image_url } | null
  onSelect,           // (product) => void
  onClear,            // () => void
  onSkip,             // () => void
  onNext,             // () => void
  catalog = [],       // pre-fetched /api/products list, used for curated picks
  optionalIngredientText = "",
  onOptionalIngredientChange,
  onProductNameChange,         // (typedName) => void — captures the typed brand
                               // so a pasted (not-in-catalog) product persists
                               // with a real name for admin review.
  // Photo OCR — opens the page-level PhotoCapture targeting THIS slot.
  // Photo result is piped into `optionalIngredientText` by the parent.
  photoUsage,                  // { used, limit, remaining } | null
  onScanPhoto,                 // () => void
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  // Controlled "paste ingredient list" disclosure so the not-found card can
  // open it programmatically.
  const [pasteOpen, setPasteOpen] = useState(false);
  // Resolved image for the currently-selected product. Starts null so the
  // monogram renders instantly; swaps to the real URL once the resolver
  // (or module cache, or product.image_url) returns.
  const [resolvedImage, setResolvedImage] = useState(null);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  // Image resolver — non-blocking. Order of preference:
  //   1. selected.image_url          (already cached on the product row)
  //   2. _IMG_CACHE[selected.id]     (resolved earlier this session)
  //   3. GET /api/products/{id}/image (Open Beauty Facts behind a write-through cache)
  // If lookup fails, we fall back to the brand-initial monogram and never
  // gate the Next button on it.
  useEffect(() => {
    if (!selected?.id) {
      setResolvedImage(null);
      return;
    }
    if (selected.image_url) {
      setResolvedImage(selected.image_url);
      _IMG_CACHE.set(selected.id, selected.image_url);
      return;
    }
    if (_IMG_CACHE.has(selected.id)) {
      setResolvedImage(_IMG_CACHE.get(selected.id));
      return;
    }
    setResolvedImage(null);
    let cancelled = false;
    (async () => {
      try {
        const { image_url } = await resolveProductImage(selected.id);
        if (cancelled) return;
        _IMG_CACHE.set(selected.id, image_url || null);
        setResolvedImage(image_url || null);
      } catch (_e) {
        if (cancelled) return;
        _IMG_CACHE.set(selected.id, null);
        setResolvedImage(null);
      }
    })();
    return () => { cancelled = true; };
  }, [selected?.id, selected?.image_url]);

  // Hard slot-filter: every suggestion shown for this step is constrained
  // to products classified into the current slot. This is what guarantees
  // no cross-category leakage (cleanser step never returns moisturizers, SPF
  // step never returns serums, etc).
  const slotProducts = useMemo(
    () => catalog.filter((p) => classifyProductSlot(p, slot)),
    [catalog, slot],
  );

  // Brands available within the current slot — used for fuzzy "did you mean"
  // suggestions. Casing is preserved so we can render the exact label back.
  const slotBrands = useMemo(() => {
    const seen = new Map();
    for (const p of slotProducts) {
      const b = (p.brand || "").trim();
      if (b && !seen.has(b.toLowerCase())) seen.set(b.toLowerCase(), b);
    }
    return [...seen.values()];
  }, [slotProducts]);

  // Curated picks are no longer surfaced before the user types — keeps the
  // input clean and prevents the popover from appearing on focus.

  // Decide whether the current query is a "broad" keyword for this slot
  // ("cleanser", "moisturizer", etc.) — these still bypass brand-matching
  // and surface the slot-filtered subset directly so users can browse.
  const isBroadTerm = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return false;
    const kws = SLOT_KEYWORDS[slot] || [];
    return kws.some((k) => k === q || (q.length <= k.length + 1 && k.startsWith(q)));
  }, [query, slot]);

  // Slot-scoped ranker: uses the centralized ranker in routineRanking.js
  // which combines match-quality tiers + brand-prestige weighting, plus
  // always-on "Did you mean Brand?" fuzzy hint (independent of result count).
  const { searchItems, didYouMean } = useMemo(() => {
    const q = query.trim();
    if (q.length < 2 || selected) {
      return { searchItems: [], didYouMean: null };
    }
    // Broad slot keyword ("cleanser", "moisturizer", "spf", …) → show all
    // products that classify into this slot, sorted by brand prestige so
    // the most recognizable picks surface first.
    if (isBroadTerm) {
      return {
        searchItems: sortByPrestige(slotProducts).slice(0, 8),
        didYouMean: null,
      };
    }

    const ranked = rankProducts(slotProducts, q).slice(0, 8);
    const dym = suggestBrand(slotBrands, q);
    // Only show the "Did you mean?" hint if the top result isn't already
    // the suggested brand — avoids noise when results are already correct.
    const topBrand = (ranked[0]?.brand || "").toLowerCase();
    const showDym =
      dym && dym.brand.toLowerCase() !== topBrand
        ? dym.brand
        : null;

    return { searchItems: ranked, didYouMean: showDym };
  }, [query, isBroadTerm, slotProducts, slotBrands, selected]);

  // Close on outside-click — but only on actual *clicks*, never on
  // mousedown over a scrollbar (browsers fire mousedown on the html/body
  // element when the user grabs the page scrollbar). This was previously
  // closing the dropdown when the user wheel-scrolled the page. We also
  // close on Escape and on focusout of the input wrapper.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      // Ignore clicks on document scrollbars (clientX > viewport width).
      if (e.clientX && e.clientX >= document.documentElement.clientWidth - 2) return;
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("click", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const suggestions = useMemo(() => {
    if (selected) return [];
    return searchItems;
  }, [searchItems, selected]);

  const handlePick = (p) => {
    onSelect?.(p);
    setQuery("");
    setOpen(false);
  };

  return (
    <section
      data-testid={`routine-step-card-${slot}`}
      className="rounded-3xl bg-white border border-surface-line p-6 sm:p-7 shadow-card scroll-mt-24 sm:scroll-mt-28"
    >
      {/* Step heading */}
      <div className="flex items-baseline justify-between mb-1">
        <p className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-peach-700">
          Step {stepNumber} of {totalSteps}
        </p>
        <p className="text-[10.5px] uppercase tracking-[0.18em] text-ink-light font-medium">
          Optional
        </p>
      </div>
      <h2 className="text-2xl sm:text-3xl font-extrabold tracking-[-0.02em] text-ink">
        {label}
      </h2>
      <p className="text-sm text-ink-mute mt-0.5">{hint}</p>
      <p className="text-[12.5px] text-ink-light mt-3 italic" data-testid={`routine-step-helper-${slot}`}>
        If you don't use this, you can skip this step.
      </p>

      {/* Input with autocomplete */}
      {!selected && (
        <div ref={wrapRef} className="mt-5 relative">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-light pointer-events-none" />
            <input
              ref={inputRef}
              data-testid={`routine-step-search-${slot}`}
              value={query}
              onChange={(e) => { setQuery(e.target.value); setOpen(true); onProductNameChange?.(e.target.value); }}
              onFocus={() => setOpen(true)}
              placeholder={`Search ${label.toLowerCase()} or type a brand…`}
              className="w-full h-14 pl-11 pr-4 rounded-2xl bg-cream-50 border border-surface-line
                         text-[15px] text-ink placeholder:text-ink-light
                         focus:outline-none focus:ring-4 focus:ring-peach-100 focus:border-peach-300
                         transition-all"
              autoComplete="off"
            />
          </div>

          {/* Suggestion popover — only opens after 2+ chars typed; never on focus. */}
          {open && query.trim().length >= 2 && (
            <div
              data-testid={`routine-step-suggestions-${slot}`}
              className="absolute left-0 right-0 top-full mt-2 rounded-2xl bg-white border border-surface-line shadow-floating overflow-hidden z-20"
            >
              {didYouMean && (
                <div
                  data-testid={`routine-did-you-mean-${slot}`}
                  className="px-4 py-2.5 text-[12.5px] text-ink-mute border-b border-surface-line bg-cream-50/60"
                >
                  Did you mean{" "}
                  <button
                    type="button"
                    onClick={() => setQuery(didYouMean)}
                    className="font-semibold text-ink underline underline-offset-2 decoration-ink-light hover:decoration-ink"
                  >
                    {didYouMean}
                  </button>
                  ?
                </div>
              )}
              {suggestions.map((p) => {
                const { brand: bShow, name: nShow } = splitBrandAndName(p);
                return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => handlePick(p)}
                  data-testid={`routine-suggestion-${p.id}`}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-cream-50 transition-colors text-left"
                >
                  {/* Suggestions are intentionally text-only — product images
                      only appear inside the selected-product card after pick. */}
                  <span className="flex-1 min-w-0">
                    {bShow && (
                      <span className="block text-[10.5px] uppercase tracking-[0.16em] font-medium text-ink-mute truncate">
                        {bShow}
                      </span>
                    )}
                    <span className="block text-[14.5px] font-semibold text-ink truncate">
                      {nShow}
                    </span>
                  </span>
                  <ChevronRight className="w-4 h-4 text-ink-light shrink-0" />
                </button>
                );
              })}
              {suggestions.length === 0 && (
                <div
                  data-testid={`routine-step-notfound-${slot}`}
                  className="px-4 py-4 space-y-3"
                >
                  <p className="text-[13.5px] text-ink leading-relaxed">
                    We couldn't find that product. Paste its ingredient list or scan the label.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {onScanPhoto && (
                      <button
                        type="button"
                        data-testid={`routine-step-notfound-scan-${slot}`}
                        onClick={() => { setOpen(false); onScanPhoto(); }}
                        className="inline-flex items-center gap-1.5 px-3.5 h-9 rounded-full border border-surface-line bg-cream-50 text-ink text-[12.5px] font-semibold hover:bg-peach-50 hover:text-peach-700 transition-colors"
                      >
                        <ImageIcon className="w-3.5 h-3.5" />
                        Scan label
                      </button>
                    )}
                    <button
                      type="button"
                      data-testid={`routine-step-notfound-paste-${slot}`}
                      onClick={() => {
                        setOpen(false);
                        setPasteOpen(true);
                        setTimeout(() => {
                          document.querySelector(`[data-testid="routine-step-paste-textarea-${slot}"]`)?.focus();
                        }, 60);
                      }}
                      className="inline-flex items-center justify-center px-3.5 h-9 rounded-full bg-ink text-cream-50 text-[12.5px] font-bold hover:bg-ink-soft transition-colors"
                    >
                      Paste ingredient list
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Selected product card */}
      {selected && (
        <div
          data-testid={`routine-step-selected-${slot}`}
          className="mt-5 rounded-2xl bg-cream-50 border border-surface-line p-4 flex items-center gap-4"
        >
          <ProductMonogram
            brand={selected.brand}
            image_url={resolvedImage}
          />
          <div className="flex-1 min-w-0">
            {(() => {
              const { brand: sBrand, name: sName } = splitBrandAndName(selected);
              return (
                <>
                  {sBrand && (
                    <p className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-ink-light">
                      {sBrand}
                    </p>
                  )}
                  <p className="text-[15.5px] font-bold text-ink truncate mt-0.5">
                    {sName}
                  </p>
                </>
              );
            })()}
          </div>
          <button
            type="button"
            onClick={onClear}
            data-testid={`routine-step-clear-${slot}`}
            className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full text-ink-mute hover:bg-white hover:text-ink transition-colors"
            aria-label="Remove selected product"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Optional ingredient list — collapsed by default; only shown if user
          wants to paste an INCI list (e.g. for products not in the catalog). */}
      {onScanPhoto && (
        <div className="mt-5 flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={onScanPhoto}
            data-testid={`routine-step-photo-${slot}`}
            title={
              photoUsage
                ? `${photoUsage.remaining} of ${photoUsage.limit} free photo scans remaining`
                : "Scan ingredient list from a photo"
            }
            className={cn(
              "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition-all text-[12.5px] font-semibold",
              photoUsage && photoUsage.remaining === 0
                ? "bg-amber-50 text-amber-900 border-amber-200 hover:bg-amber-100"
                : "bg-cream-50 text-ink border-surface-line hover:bg-peach-50 hover:text-peach-700",
            )}
          >
            <ImageIcon className="w-3.5 h-3.5" />
            Scan photo
            {photoUsage && (
              <span
                data-testid={`routine-step-photo-counter-${slot}`}
                className={cn(
                  "ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full text-[10px] font-bold leading-none",
                  photoUsage.remaining === 0
                    ? "bg-amber-200 text-amber-900"
                    : photoUsage.remaining === 1
                      ? "bg-amber-100 text-amber-900 border border-amber-200"
                      : "bg-white text-ink-mute border border-surface-line",
                )}
              >
                {photoUsage.remaining === 0
                  ? "0 left"
                  : `${photoUsage.remaining} left`}
              </span>
            )}
          </button>
          <span className="text-[12px] text-ink-light">or paste below</span>
        </div>
      )}

      <details
        className={cn("group", onScanPhoto ? "mt-3" : "mt-5")}
        data-testid={`routine-step-paste-${slot}`}
        open={pasteOpen}
        onToggle={(e) => setPasteOpen(e.currentTarget.open)}
      >
        <summary className="cursor-pointer text-[12.5px] font-semibold text-ink-soft hover:text-ink select-none inline-flex items-center gap-1.5 list-none">
          <span className="inline-block w-3 group-open:rotate-90 transition-transform">›</span>
          Paste ingredient list instead
        </summary>
        <textarea
          value={optionalIngredientText}
          onChange={(e) => onOptionalIngredientChange?.(e.target.value)}
          data-testid={`routine-step-paste-textarea-${slot}`}
          placeholder="Paste INCI ingredients, comma-separated"
          rows={3}
          className="mt-3 w-full px-4 py-3 rounded-xl bg-cream-50 border border-surface-line
                     text-sm text-ink placeholder:text-ink-light resize-none
                     focus:outline-none focus:ring-4 focus:ring-peach-100 focus:border-peach-300"
        />
      </details>

      {/* Bottom actions — a single, unambiguous forward control. An empty
          optional step reads "Skip step"; a filled step reads "Next"; the
          final step runs the check. (Previously a second standalone "Skip"
          button duplicated this and became a dead control on the last step.) */}
      <div className="mt-6 flex items-center justify-end gap-3">
        <Button
          type="button"
          onClick={onNext}
          data-testid={`routine-step-next-${slot}`}
          className="h-12 px-6 text-[14px] font-bold"
        >
          {isLast ? (
            <>Check routine <Sparkles className="w-4 h-4 ml-1.5" /></>
          ) : selected || optionalIngredientText.trim() ? (
            <>Next <ChevronRight className="w-4 h-4 ml-1" /></>
          ) : (
            <>Skip step <ChevronRight className="w-4 h-4 ml-1" /></>
          )}
        </Button>
      </div>
    </section>
  );
}
